import { inject, Service } from '@angular/core';
import { ChecklistItemStatus } from '../../modules/checklist/types/ChecklistItemStatus.enum';
import {
  ChecklistStatus,
  isTerminalChecklistStatus,
} from '../../modules/checklist/types/ChecklistStatus.enum';
import { ChecklistItem } from '../../modules/checklist/types/ChecklistTypes';
import {
  clearedItemPatch,
  createUpdatedChecklist,
  customInputPayloadPatch,
  findItem,
  mergeFieldData,
  slotSelectionPayloadPatch,
  withoutItem,
} from '../../modules/checklist/utils/checklist-patches';
import { ChecklistFileRefsService } from '../checklist-file-refs.service';
import { ChecklistStoreService } from '../checklist-store.service';
import type { ChecklistAction } from './checklist-detail-hub.client';

@Service()
export class LiveChecklistActionService {
  private readonly store = inject(ChecklistStoreService);
  private readonly fileRefs = inject(ChecklistFileRefsService);

  applyLiveChecklistAction(action: ChecklistAction): void {
    switch (action.type) {
      case 'checklistCompleted':
        this.applyLiveTerminalStatus(
          action.checklistId,
          ChecklistStatus.Completed
        );
        return;
      case 'checklistDiscarded':
        this.applyLiveTerminalStatus(
          action.checklistId,
          ChecklistStatus.Discarded
        );
        return;
      case 'checklistItemStatusChanged':
        this.patchLiveChecklistItem(action, item => ({
          status: action.status,
          statusChanged: action.statusChanged,
          statusChangedBy: action.author,
          // Same precedence as the backend's CompletionTime, where an undo-preserved or edited time outranks the status stamp.
          completionTime:
            item.undoTime ?? item.entryTime ?? action.statusChanged,
        }));
        return;
      case 'checklistItemContentsCleared':
        this.patchLiveChecklistItem(action, item => clearedItemPatch(item));
        return;
      case 'checklistItemUndoKept':
        this.patchLiveChecklistItem(action, () => ({
          status: ChecklistItemStatus.Unchecked,
          statusChanged: undefined,
          statusChangedBy: undefined,
          entryTime: action.entryTime,
          undoTime: action.undoTime,
          completionTime: action.undoTime ?? action.entryTime,
        }));
        return;
      case 'checklistItemRemarksUpdated':
        this.patchLiveChecklistItem(action, () => ({
          remarks: action.remarks,
        }));
        return;
      case 'checklistItemHeaderUpdated':
        this.patchLiveChecklistItem(action, () => ({ header: action.header }));
        return;
      case 'checklistItemCompletionTimeUpdated':
        this.patchLiveChecklistItem(action, item => ({
          entryTime: action.entryTime,
          undoTime: undefined,
          completionTime: action.entryTime ?? item.statusChanged,
        }));
        return;
      case 'checklistItemCustomInputUpdated':
        this.patchLiveChecklistItem(action, item =>
          customInputPayloadPatch(
            item,
            action.customInputId,
            action.payload,
            item.customInputs?.find(
              candidate => candidate.id === action.customInputId
            )?.slotId
          )
        );
        return;
      case 'checklistItemSlotSelectionUpdated':
        this.patchLiveChecklistItem(action, item =>
          slotSelectionPayloadPatch(item, action.slotId, action.payload)
        );
        return;
      case 'checklistItemFieldUpdated':
        this.patchLiveChecklistItem(action, item => ({
          fields: (item.fields ?? []).map(field =>
            field.id === action.fieldId
              ? {
                  ...field,
                  data: mergeFieldData(field.data, action.value),
                }
              : field
          ),
        }));
        return;
      case 'checklistItemFileAttached': {
        const { file } = action;
        this.fileRefs.mutateImageUploadFileRefs(
          action.checklistId,
          action.itemId,
          action.customInputId,
          files =>
            files.some(candidate => candidate.id === file.id)
              ? files
              : [...files, file]
        );
        return;
      }
      case 'checklistItemFileRemoved':
        if (action.customInputId) {
          this.fileRefs.removeImageUploadFileRef(
            action.checklistId,
            action.itemId,
            action.customInputId,
            action.fileId
          );
        }
        return;
      case 'checklistRepeatableItemAdded':
        this.addLiveRepeatableItem(action.checklistId, action.item);
        return;
      case 'checklistRepeatableItemDiscarded': {
        const checklist = this.store.latest(action.checklistId);
        if (checklist) {
          this.store.update(
            withoutItem(checklist, action.sectionId, action.itemId)
          );
        }
        return;
      }
    }
  }

  private applyLiveTerminalStatus(
    checklistId: string,
    status: ChecklistStatus
  ): void {
    const checklist = this.store.latest(checklistId);
    if (!checklist || isTerminalChecklistStatus(checklist.status)) {
      return;
    }

    this.store.update({ ...checklist, status });
  }

  private addLiveRepeatableItem(
    checklistId: string,
    item: ChecklistItem
  ): void {
    const checklist = this.store.latest(checklistId);
    const section = checklist?.sections.find(s => s.id === item.sectionId);
    if (!checklist || !section) {
      return;
    }
    if (section.items.some(candidate => candidate.id === item.id)) {
      return;
    }

    this.store.update({
      ...checklist,
      sections: checklist.sections.map(s =>
        s.id === item.sectionId ? { ...s, items: [...s.items, item] } : s
      ),
    });
  }

  private patchLiveChecklistItem(
    target: { checklistId: string; sectionId: string; itemId: string },
    patchFor: (item: ChecklistItem) => Partial<ChecklistItem>
  ): void {
    const checklist = this.store.latest(target.checklistId);
    const item =
      checklist && findItem(checklist, target.sectionId, target.itemId);
    if (!checklist || !item) {
      return;
    }

    this.store.update(
      createUpdatedChecklist(
        checklist,
        target.sectionId,
        target.itemId,
        patchFor(item)
      )
    );
  }
}
